import { useEffect, useMemo, useRef } from 'react';
import type { ConfiguredService } from '../types/service.types';
import type {
    SmriqGeneratedService,
    SmriqPersistedInclusions,
    SmriqWorkItem,
} from '../types/smriq.types';

interface UseSmriqInclusionsByVisitArgs {
    generatedServices: SmriqGeneratedService[];
    configuredServices: ConfiguredService[];
    reconcile: (services: { id: string; declined_items: SmriqWorkItem[] }[]) => void;
}

export function useSmriqInclusionsByVisit({
    generatedServices,
    configuredServices,
    reconcile,
}: UseSmriqInclusionsByVisitArgs): Record<string, SmriqPersistedInclusions> {
    // The in-memory generate response wins; a resumed quote with none falls back to the
    // inclusions persisted on each visit, so the decline UI restores without a vendor call.
    const inclusionsByVisit = useMemo(() => {
        if (generatedServices.length > 0) {
            return Object.fromEntries(
                generatedServices.map((service) => [
                    service.id,
                    { items: service.items, declinedItems: service.declined_items },
                ])
            );
        }
        return Object.fromEntries(
            configuredServices
                .filter((service) => service.smriqInclusions)
                .map((service) => [
                    service.instanceId,
                    {
                        items: service.smriqInclusions!.items,
                        declinedItems: service.smriqInclusions!.declinedItems,
                    },
                ])
        );
    }, [generatedServices, configuredServices]);

    const declinesHydratedRef = useRef(false);

    // On a resumed quote (no in-memory generate response) seed the decline set once from the
    // persisted inclusions. A fresh generate reconciles from its own response instead.
    useEffect(() => {
        if (declinesHydratedRef.current || generatedServices.length > 0) return;
        const restored = configuredServices.filter((service) => service.smriqInclusions);
        if (restored.length === 0) return;
        declinesHydratedRef.current = true;
        reconcile(
            restored.map((service) => ({
                id: service.instanceId,
                declined_items: service.smriqInclusions!.declinedItems,
            }))
        );
    }, [generatedServices.length, configuredServices, reconcile]);

    return inclusionsByVisit;
}
